import { supabase } from './supabaseClient';

export interface AssinaturaDigital {
    id: string;
    lancamento_id: number;
    token_acesso: string;
    codigo_verificacao: string | null;
    status: 'PENDENTE' | 'ASSINADO' | 'CANCELADO';
    email_assinante: string;
    nome_assinante: string;
    dados_assinatura: any | null;
    arquivo_assinado_path: string | null;
    arquivo_rascunho_path?: string | null;
    created_at: string;
    updated_at: string;
    data_assinatura: string | null;
    lancamentos_financeiros?: any;
    hash_curto?: string;
    telefone_assinante?: string | null;
    metodo_envio?: string;
}

export const getAssinaturas = async (status?: string): Promise<AssinaturaDigital[]> => {
    let query = supabase
        .from('assinaturas_digitais')
        .select(`
            *,
            lancamentos_financeiros (
                id, data_lancamento, obra_id, desconto_valor,
                obras (nome),
                fornecedores (nome_razao_social),
                lancamento_itens (
                    quantidade, valor_unitario, desconto_valor
                )
            )
        `)
        .order('created_at', { ascending: false });

    if (status) {
        query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    // Calculate valor_total_liquido for each signature
    const processedData = data.map((ass: any) => {
        if (ass.lancamentos_financeiros) {
            const lf = ass.lancamentos_financeiros;
            const items = lf.lancamento_itens || [];
            let itemsSum = 0;
            for (const item of items) {
                itemsSum += (item.quantidade * item.valor_unitario) - (item.desconto_valor || 0);
            }
            lf.valor_total_liquido = itemsSum - (lf.desconto_valor || 0);
        }
        return ass;
    });

    return processedData as AssinaturaDigital[];
};

export const gerarLinkAssinatura = async (
    lancamentoId: number, 
    email: string, 
    nome: string, 
    arquivo_rascunho_path?: string,
    telefone?: string | null,
    metodoEnvio: string = 'email'
): Promise<{ link: string, hash_curto: string }> => {
    
    // Verificar se já existe uma assinatura PENDENTE para este lancamento
    const { data: existing } = await supabase
        .from('assinaturas_digitais')
        .select('id, token_acesso, hash_curto, arquivo_rascunho_path')
        .eq('lancamento_id', lancamentoId)
        .eq('status', 'PENDENTE')
        .maybeSingle();

    let signatureData;

    if (existing) {
        // Atualiza a assinatura pendente existente
        const { data: updated, error: updateError } = await supabase
            .from('assinaturas_digitais')
            .update({
                email_assinante: email,
                nome_assinante: nome,
                telefone_assinante: telefone,
                metodo_envio: metodoEnvio,
                arquivo_rascunho_path: arquivo_rascunho_path || existing.arquivo_rascunho_path
            })
            .eq('id', existing.id)
            .select('token_acesso, hash_curto, id')
            .single();

        if (updateError) throw updateError;
        signatureData = updated;
    } else {
        // Cria uma nova
        const hash_curto = Math.random().toString(36).substring(2, 8);
        const { data: inserted, error: insertError } = await supabase
            .from('assinaturas_digitais')
            .insert({
                lancamento_id: lancamentoId,
                email_assinante: email,
                nome_assinante: nome,
                status: 'PENDENTE',
                arquivo_rascunho_path: arquivo_rascunho_path,
                hash_curto: hash_curto,
                telefone_assinante: telefone,
                metodo_envio: metodoEnvio
            })
            .select('token_acesso, hash_curto, id')
            .single();

        if (insertError) throw insertError;
        signatureData = inserted;
    }

    // Acionar a Edge Function para enviar o convite (fire and forget)
    supabase.functions.invoke('enviar-link-assinatura', {
        body: { assinatura_id: signatureData.id, token_acesso: signatureData.token_acesso, hash_curto: signatureData.hash_curto }
    }).catch(console.error);
    
    // Construct the public URL
    const domain = window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://assinaturas-sistema.com.br';
    return {
        link: `${domain}/s/${signatureData.hash_curto}`,
        hash_curto: signatureData.hash_curto
    };
};

export const cancelarAssinatura = async (id: string): Promise<void> => {
    const { error } = await supabase
        .from('assinaturas_digitais')
        .update({ status: 'CANCELADO' })
        .eq('id', id);

    if (error) throw error;
};

export const getAssinaturaPublica = async (token: string): Promise<any> => {
    const { data, error } = await supabase.functions.invoke('get-assinatura-publica', {
        body: { token }
    });

    if (error) {
        // A função já retorna 406 para erro/não encontrado
        throw new Error('Assinatura não encontrada ou link inválido.');
    }

    if (data && data.lancamentos_financeiros) {
        const lf = data.lancamentos_financeiros;
        const items = lf.lancamento_itens || [];
        let itemsSum = 0;
        for (const item of items) {
            itemsSum += (item.quantidade * item.valor_unitario) - (item.desconto_valor || 0);
        }
        lf.valor_total_liquido = itemsSum - (lf.desconto_valor || 0);
    }

    return data;
};

export const solicitarCodigoOtp = async (token: string): Promise<void> => {
    const { data, error } = await supabase.functions.invoke('enviar-codigo-assinatura', {
        body: { token }
    });
    if (error) throw error;
    if (data && data.success === false) throw new Error(data.error || 'Erro ao enviar código.');
};

export const confirmarAssinatura = async (token: string, codigo: string, nome: string): Promise<void> => {
    const { data, error } = await supabase.functions.invoke('confirmar-assinatura', {
        body: { token, codigo, nome_digitado: nome }
    });
    if (error) throw error;
    if (data && data.success === false) throw new Error(data.error || 'Erro ao confirmar assinatura.');
};

export const getAssinaturaByHash = async (hash: string): Promise<any> => {
    const { data, error } = await supabase.functions.invoke('get-assinatura-publica', {
        body: { hash }
    });

    if (error) {
        throw new Error('Assinatura não encontrada ou link inválido.');
    }

    return data;
};

export const getAssinaturaByHashCurto = async (hash_curto: string): Promise<any> => {
    const { data, error } = await supabase.functions.invoke('get-assinatura-publica', {
        body: { hash_curto }
    });

    if (error) {
        throw new Error('Assinatura não encontrada ou link inválido.');
    }

    if (data && data.lancamentos_financeiros) {
        const lf = data.lancamentos_financeiros;
        const items = lf.lancamento_itens || [];
        let itemsSum = 0;
        for (const item of items) {
            itemsSum += (item.quantidade * item.valor_unitario) - (item.desconto_valor || 0);
        }
        lf.valor_total_liquido = itemsSum - (lf.desconto_valor || 0);
    }

    return data;
};

export const getAssinaturaAtivaByLancamentoId = async (lancamentoId: number): Promise<{ id: string, hash_curto: string, status: 'PENDENTE' | 'ASSINADO' } | null> => {
    const { data, error } = await supabase
        .from('assinaturas_digitais')
        .select('id, hash_curto, status')
        .eq('lancamento_id', lancamentoId)
        .in('status', ['PENDENTE', 'ASSINADO'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error) throw error;
    return data as any;
};
